import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Custom hook for keyboard shortcuts
 * Provides global keyboard shortcuts for admin panel navigation
 */
export const useKeyboardShortcuts = () => {
  const navigate = useNavigate();

  const handleKeyPress = useCallback((event) => {
    // Only activate shortcuts when not typing in inputs
    if (event.target.tagName === 'INPUT' || 
        event.target.tagName === 'TEXTAREA' || 
        event.target.contentEditable === 'true') {
      return;
    }

    // Ctrl/Cmd + key combinations
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'h': // Ctrl+H - Home/Dashboard
          event.preventDefault();
          navigate('/admin');
          break;
        case 'p': // Ctrl+P - Products
          event.preventDefault();
          navigate('/admin/products');
          break;
        case 'o': // Ctrl+O - Orders
          event.preventDefault();
          navigate('/admin/orders');
          break;
        case 'u': // Ctrl+U - Customers (Users)
          event.preventDefault();
          navigate('/admin/customers');
          break;
        case 'b': // Ctrl+B - Companies (Business)
          event.preventDefault();
          navigate('/admin/companies');
          break;
        case 'k': // Ctrl+K - Global search
          event.preventDefault();
          showGlobalSearch();
          break;
        case 'n': // Ctrl+N - New (context-dependent)
          event.preventDefault();
          handleNewAction();
          break;
        case '/': // Ctrl+/ - Show shortcuts help
          event.preventDefault();
          showShortcutsHelp();
          break;
        default:
          break;
      }
    }

    // Alt + key combinations
    if (event.altKey) {
      switch (event.key) {
        case 'f': // Alt+F - Toggle filters
          event.preventDefault();
          toggleFilters();
          break;
        case 's': // Alt+S - Focus search
          event.preventDefault();
          focusSearch();
          break;
        case 'n': // Alt+N - Notifications
          event.preventDefault();
          toggleNotifications();
          break;
        default:
          break;
      }
    }

    // Single key shortcuts (when not in input)
    if (!event.ctrlKey && !event.metaKey && !event.altKey) {
      switch (event.key) {
        case 'Escape': // ESC - Close modals/overlays
          event.preventDefault();
          closeModals();
          break;
        case '?': // ? - Show help
          event.preventDefault();
          showShortcutsHelp();
          break;
        default:
          break;
      }
    }
  }, [navigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  // Helper functions
  const showGlobalSearch = () => {
    // Trigger global search modal
    const searchInput = document.querySelector('[data-global-search]');
    if (searchInput) {
      searchInput.focus();
    } else {
      // Create and show search modal if it doesn't exist
      const event = new CustomEvent('showGlobalSearch');
      document.dispatchEvent(event);
    }
  };

  const handleNewAction = () => {
    // Context-dependent new action based on current page
    const currentPath = window.location.pathname;
    
    if (currentPath.includes('/products')) {
      const event = new CustomEvent('addProduct');
      document.dispatchEvent(event);
    } else if (currentPath.includes('/orders')) {
      // Navigate to create order if we had such functionality
    } else if (currentPath.includes('/customers')) {
      const event = new CustomEvent('addCustomer');
      document.dispatchEvent(event);
    } else if (currentPath.includes('/companies')) {
      const event = new CustomEvent('addCompany');
      document.dispatchEvent(event);
    }
  };

  const toggleFilters = () => {
    const event = new CustomEvent('toggleFilters');
    document.dispatchEvent(event);
  };

  const focusSearch = () => {
    const searchInputs = document.querySelectorAll('input[name="search"]');
    if (searchInputs.length > 0) {
      searchInputs[0].focus();
    }
  };

  const toggleNotifications = () => {
    const event = new CustomEvent('toggleNotifications');
    document.dispatchEvent(event);
  };

  const closeModals = () => {
    const event = new CustomEvent('closeAllModals');
    document.dispatchEvent(event);
  };

  const showShortcutsHelp = () => {
    const event = new CustomEvent('showShortcutsHelp');
    document.dispatchEvent(event);
  };
};

/**
 * Keyboard Shortcuts Help Component
 */
export const KeyboardShortcutsHelp = ({ isOpen, onClose }) => {
  const shortcuts = [
    {
      category: 'ניווט',
      shortcuts: [
        { keys: ['Ctrl', 'H'], description: 'דשבורד ראשי' },
        { keys: ['Ctrl', 'P'], description: 'עמוד מוצרים' },
        { keys: ['Ctrl', 'O'], description: 'עמוד הזמנות' },
        { keys: ['Ctrl', 'U'], description: 'עמוד לקוחות' },
        { keys: ['Ctrl', 'B'], description: 'עמוד חברות' }
      ]
    },
    {
      category: 'פעולות',
      shortcuts: [
        { keys: ['Ctrl', 'K'], description: 'חיפוש גלובלי' },
        { keys: ['Ctrl', 'N'], description: 'יצירת פריט חדש' },
        { keys: ['Alt', 'F'], description: 'הצג/הסתר מסננים' },
        { keys: ['Alt', 'S'], description: 'התמקד בחיפוש' },
        { keys: ['Alt', 'N'], description: 'פתח התראות' }
      ]
    },
    {
      category: 'כללי',
      shortcuts: [
        { keys: ['Esc'], description: 'סגור מודלים' },
        { keys: ['?'], description: 'הצג עזרה זו' },
        { keys: ['Ctrl', '/'], description: 'הצג עזרה זו' }
      ]
    }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-right overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="w-full">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  קיצורי מקלדת
                </h3>
                
                <div className="space-y-6">
                  {shortcuts.map((category) => (
                    <div key={category.category}>
                      <h4 className="text-sm font-medium text-gray-700 mb-3 border-b pb-2">
                        {category.category}
                      </h4>
                      <div className="space-y-2">
                        {category.shortcuts.map((shortcut, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              {shortcut.description}
                            </span>
                            <div className="flex items-center space-x-1 space-x-reverse">
                              {shortcut.keys.map((key, keyIndex) => (
                                <React.Fragment key={keyIndex}>
                                  {keyIndex > 0 && (
                                    <span className="text-gray-400 text-xs">+</span>
                                  )}
                                  <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-md">
                                    {key}
                                  </kbd>
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={onClose}
            >
              סגור
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default useKeyboardShortcuts;
